import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import OscopLogo from './brand/OscopLogo';
import { Shield, Search, Calendar, X, Loader2, Clock } from 'lucide-react';
import { CATEGORIES_EXPERTISE } from './expertsValidationShared';
import { ExpertCard } from './ExpertCard';
import { ExpertDialogs } from './ExpertDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Catégories d'expertise (déplacées vers expertsValidationShared.js)

export default function ExpertsValidationAmeliore() {
  const { getAuthHeader } = useAuth();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validatingId, setValidatingId] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [action, setAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');

  // Checklist de validation
  const [checklist, setChecklist] = useState({
    qualifications_verifiees: false,
    experience_adequate: false,
    documents_valides: false,
    references_verifiees: false
  });

  useEffect(() => {
    fetchExperts();
  }, []);

  const fetchExperts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/experts/en-attente`, {
        headers: getAuthHeader()
      });
      setExperts(response.data?.experts || response.data || []);
    } catch {
      toast.error('Erreur lors du chargement des experts en attente');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetailModal = (expert) => {
    setSelectedExpert(expert);
    setOpenDetailModal(true);
    setChecklist({
      qualifications_verifiees: false,
      experience_adequate: false,
      documents_valides: false,
      references_verifiees: false
    });
  };

  const handleOpenDialog = (expert, actionType) => {
    setSelectedExpert(expert);
    setAction(actionType);
    setRejectionReason('');
    setOpenDialog(true);
  };

  const handleValidation = async () => {
    if (action === 'reject' && !rejectionReason.trim()) {
      toast.error('Veuillez indiquer la raison du rejet');
      return;
    }

    if (action === 'validate') {
      const allChecked = Object.values(checklist).every(v => v === true);
      if (!allChecked) {
        toast.error('Veuillez cocher tous les points de la checklist avant de valider');
        return;
      }
    }

    try {
      setValidatingId(selectedExpert.id);
      
      const validationData = {
        statut: action === 'validate' ? 'validé' : 'rejeté',
        commentaire: action === 'reject' ? rejectionReason : 'Validé par l\'administrateur'
      };

      await axios.patch(
        `${API}/experts/${selectedExpert.id}/validation`,
        validationData,
        { headers: getAuthHeader() }
      );

      toast.success(
        action === 'validate' 
          ? "COOPER'S Advisor validé avec succès ✅" 
          : "COOPER'S Advisor rejeté"
      );
      
      setOpenDialog(false);
      setOpenDetailModal(false);
      fetchExperts();
    } catch {
      toast.error('Erreur lors de la validation');
    } finally {
      setValidatingId(null);
    }
  };

  // Filtrage
  const getFilteredExperts = () => {
    let filtered = [...experts];

    if (searchQuery.trim()) {
      filtered = filtered.filter(e =>
        e.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.prenom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.raison_sociale?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(e => 
        e.categories_expertise?.includes(categoryFilter)
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.date_inscription || 0);
      const dateB = new Date(b.date_inscription || 0);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const filteredExperts = getFilteredExperts();

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Filtres premium O'SCOP */}
      <Card className="bg-gradient-to-br from-[#0a3d62]/3 to-[#DEA900]/3 border border-[#0a3d62]/15 mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher par nom, prénom, email, entreprise..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200 focus:border-[#DEA900] focus:ring-[#DEA900]/20"
              />
            </div>

            <div className="flex gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px] bg-white border-slate-200">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {CATEGORIES_EXPERTISE.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                className="border-slate-200"
              >
                <Calendar className="h-4 w-4 mr-2" />
                {sortOrder === 'desc' ? 'Plus récent' : 'Plus ancien'}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-slate-700">
              <strong className="text-[#0a3d62] tabular-nums">{filteredExperts.length}</strong>
              <span className="text-slate-500"> COOPER'S Advisor(s) en attente</span>
              {searchQuery && <span className="text-slate-500"> · « {searchQuery} »</span>}
            </p>
            {(searchQuery || categoryFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                }}
                className="text-[#0a3d62] hover:bg-[#0a3d62]/5"
              >
                <X className="h-4 w-4 mr-1" />
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200/80">
        <CardHeader className="relative overflow-hidden bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] text-white p-5">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
          <div className="relative flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30 shrink-0">
              <OscopLogo size={28} variant="on-navy" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2 text-white text-lg leading-none">
                Validation COOPER'S Advisors
              </CardTitle>
              <CardDescription className="text-[#f5cc3e] text-[11px] uppercase tracking-wider mt-1.5">
                Nouvelles inscriptions en attente d'arbitrage
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Statistique premium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="relative bg-white border border-slate-200/80 rounded-2xl p-5 ring-1 ring-[#DEA900]/30 shadow-[0_8px_24px_-12px_rgba(222,169,0,0.25)]">
              <span className="absolute left-0 top-5 bottom-5 w-1 rounded-r-full bg-[#DEA900]" />
              <div className="flex items-center justify-between pl-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">En attente</p>
                  <p className="text-4xl font-bold tracking-tight tabular-nums mt-1 text-[#b88600]">{filteredExperts.length}</p>
                  <p className="text-[11px] text-slate-500 mt-1">Advisors non validés</p>
                </div>
                <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-[#DEA900]/12">
                  <Clock className="h-6 w-6 text-[#b88600]" strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </div>

          {filteredExperts.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium">
                {searchQuery || categoryFilter !== 'all'
                  ? 'Aucun expert ne correspond aux filtres'
                  : 'Aucun expert en attente de validation'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredExperts.map((expert) => (
                <ExpertCard
                  key={expert.id}
                  expert={expert}
                  onDetail={handleOpenDetailModal}
                  onValidate={(e) => { setSelectedExpert(e); handleOpenDialog(e, 'validate'); }}
                  onReject={(e) => handleOpenDialog(e, 'reject')}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Détaillé */}
      <ExpertDialogs
        openDetailModal={openDetailModal}
        setOpenDetailModal={setOpenDetailModal}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        selectedExpert={selectedExpert}
        checklist={checklist}
        setChecklist={setChecklist}
        action={action}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        validatingId={validatingId}
        handleValidation={handleValidation}
        handleOpenDialog={handleOpenDialog}
      />
    </>
  );
}
